import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useGetMyComplianceStatus } from '../../hooks/queries/moodle/useGetMyComplianceStatus';

const COMPLETION_STATE_COLORS = {
  NOT_STARTED: 'default',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  NOT_ENROLLED: 'default',
  EXCLUDED: 'default',
};

const COMPLETION_STATE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Not Started', value: 'NOT_STARTED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Not Enrolled', value: 'NOT_ENROLLED' },
  { label: 'Excluded', value: 'EXCLUDED' },
  { label: 'Overdue', value: 'OVERDUE' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const UserCourseReportsComponent = () => {
  const [stateFilter, setStateFilter] = useState('');

  const { courses, loading } = useGetMyComplianceStatus();

  const filteredCourses = useMemo(() => {
    if (!stateFilter) return courses;
    return courses.filter((course) => course.completionState === stateFilter);
  }, [courses, stateFilter]);

  const downloadAsExcel = () => {
    const rows = filteredCourses.map((row) => ({
      'Course Name': row.courseName,
      Year: row.year,
      'Completion State': row.completionState,
      Deadline: formatDateTime(row.deadlineAt),
      'Completed At': formatDateTime(row.completedAt),
      'Last Synced': formatDateTime(row.lastSyncedAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'My Course Reports');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(data, 'my_course_reports.xlsx');
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Completion State</InputLabel>
          <Select
            value={stateFilter}
            label="Completion State"
            onChange={(e) => setStateFilter(e.target.value)}
          >
            {COMPLETION_STATE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filteredCourses.length > 0 && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadAsExcel}
          >
            Download Excel
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Course Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, px: 2 }}>Year</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completion State
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Deadline
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completed At
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Last Synced
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredCourses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No course completion data found.
                </TableCell>
              </TableRow>
            ) : (
              filteredCourses.map((row, index) => (
                <TableRow key={`${row.moodleCourseId}-${index}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.courseName}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>{row.year}</TableCell>
                  <TableCell sx={{ px: 2 }}>
                    <Chip
                      label={row.completionState}
                      color={
                        COMPLETION_STATE_COLORS[row.completionState] ??
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.deadlineAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.completedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.lastSyncedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserCourseReportsComponent;
